import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Search, Menu, X, Leaf, MapPin, ShieldCheck } from "lucide-react";
import CartDrawer from "./CartDrawer";

export default function Header({ onSearch, searchValue = "" }) {
  const { lang, setLang, t, itemCount, user } = useApp();
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = !!user?.is_admin;

  const nav = [
    { to: "/", label: t("nav_home"), tid: "nav-home" },
    { to: "/kampanyalar", label: t("campaigns"), tid: "nav-campaigns" },
    { to: "/track", label: t("nav_track"), tid: "nav-track" },
    { to: "/contact", label: t("nav_contact"), tid: "nav-contact" },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF7F2]/85 border-b border-[#E8E2D5]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20 gap-4">
            {/* Logo */}
            <Link to="/" data-testid="brand-link" className="flex items-center gap-2 shrink-0">
              <img
                src="/icons/icon-192.png"
                alt="İkizler Market"
                className="w-10 h-10 rounded-full border border-[#E8E2D5] shadow-sm"
              />
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="font-heading font-extrabold text-lg text-[#1B2A22]">
                  İkizler
                </span>
                <span className="font-heading text-[10px] uppercase tracking-widest text-[#2E5A36]">
                  Market · Lefke
                </span>
              </div>
            </Link>

            {/* Search - hidden on mobile */}
            {onSearch && (
              <div className="hidden md:flex flex-1 max-w-xl relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2E5A36]" />
                <Input
                  data-testid="search-input"
                  value={searchValue}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder={t("search_placeholder")}
                  className="pl-10 h-11 bg-white border-[#E8E2D5] focus-visible:ring-[#2E5A36]"
                />
              </div>
            )}

            {/* Right actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Admin shortcut — only visible when logged in as admin.
                  Keeps the admin session persistent while browsing public pages. */}
              {isAdmin && location.pathname !== "/panel-ikz-lefke" && location.pathname !== "/admin" && (
                <Link to="/panel-ikz-lefke" data-testid="admin-shortcut-link" className="hidden sm:inline-flex">
                  <Button variant="outline" className="h-11 border-[#2E5A36] text-[#2E5A36] bg-white hover:bg-[#2E5A36]/10 rounded-full font-heading font-bold">
                    <ShieldCheck className="w-4 h-4 mr-1" /> Panel
                  </Button>
                </Link>
              )}
              {/* Language toggle */}
              <div
                data-testid="lang-toggle"
                className="hidden sm:flex items-center bg-white border border-[#E8E2D5] rounded-full p-1 gap-0.5"
              >
                {["tr", "en"].map((l) => (
                  <button
                    key={l}
                    data-testid={`lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase transition-all ${
                      lang === l
                        ? "bg-[#2E5A36] text-[#FAF7F2]"
                        : "text-[#1B2A22] hover:bg-[#FAF7F2]"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>

              {/* Cart */}
              <Button
                data-testid="cart-drawer-trigger"
                variant="outline"
                onClick={() => setCartOpen(true)}
                className="relative h-11 border-[#E8E2D5] bg-white hover:bg-[#FAF7F2] hover:border-[#2E5A36]"
              >
                <ShoppingCart className="w-4 h-4" />
                {itemCount > 0 && (
                  <Badge
                    data-testid="cart-count-badge"
                    className="absolute -top-2 -right-2 bg-[#D9653B] text-white h-5 min-w-5 px-1 rounded-full font-mono text-[11px]"
                  >
                    {itemCount}
                  </Badge>
                )}
              </Button>

              {/* Mobile menu */}
              <Button
                data-testid="mobile-menu-toggle"
                variant="outline"
                onClick={() => setMobileOpen((v) => !v)}
                className="lg:hidden h-11 w-11 p-0 border-[#E8E2D5] bg-white"
              >
                {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </Button>

              {/* Desktop nav */}
              <nav className="hidden lg:flex items-center gap-1 ml-2">
                {nav.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={n.tid}
                    className={`px-3 py-2 rounded-full text-sm font-semibold transition-all ${
                      location.pathname === n.to
                        ? "bg-[#2E5A36] text-[#FAF7F2]"
                        : "text-[#1B2A22] hover:bg-[#E8E2D5]/50"
                    }`}
                  >
                    {n.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          {/* Mobile search */}
          {onSearch && (
            <div className="md:hidden pb-3 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2E5A36]" />
              <Input
                data-testid="search-input-mobile"
                value={searchValue}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t("search_placeholder")}
                className="pl-10 h-11 bg-white border-[#E8E2D5]"
              />
            </div>
          )}

          {/* Mobile menu drawer */}
          {mobileOpen && (
            <div className="lg:hidden pb-4 flex flex-col gap-1 border-t border-[#E8E2D5] pt-3">
              {isAdmin && (
                <Link
                  to="/panel-ikz-lefke"
                  data-testid="admin-shortcut-link-mobile"
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 rounded-xl text-sm font-semibold bg-[#2E5A36]/10 text-[#2E5A36] flex items-center gap-2"
                >
                  <ShieldCheck className="w-4 h-4" /> Yönetim Paneli
                </Link>
              )}
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`${n.tid}-mobile`}
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-xl text-sm font-semibold ${
                    location.pathname === n.to
                      ? "bg-[#2E5A36] text-[#FAF7F2]"
                      : "text-[#1B2A22] hover:bg-[#E8E2D5]/50"
                  }`}
                >
                  {n.label}
                </Link>
              ))}
              <div className="flex items-center gap-2 px-4 py-3">
                <span className="text-xs font-semibold uppercase text-[#2E5A36]">Dil:</span>
                {["tr", "en"].map((l) => (
                  <button
                    key={l}
                    data-testid={`lang-${l}-mobile`}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                      lang === l ? "bg-[#2E5A36] text-[#FAF7F2]" : "bg-white border border-[#E8E2D5]"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </>
  );
}
